"use client"
import { useRef, useEffect, useState, forwardRef, useImperativeHandle } from "react"
import { RandomTower } from "@/components/random-tower"
import type { RandomTowerRef } from "@/types/tower"
import { HOTKEYS, matchesPhysicalKey } from "@/lib/keyboard-utils"
import { debug } from "@/lib/debug"

// Change to use forwardRef and useImperativeHandle
export const MobileRandomTower = forwardRef<RandomTowerRef, {}>((props, ref) => {
  const randomTowerRef = useRef<RandomTowerRef | null>(null)
  const [towerHeight, setTowerHeight] = useState(240)
  const [isGenerating, setIsGenerating] = useState(false)

  // Pre-allocate space for the tower to prevent layout shifts
  const [maxTowerHeight, setMaxTowerHeight] = useState(320) // Max height 320px

  // Forward the ref methods to the parent component
  useImperativeHandle(
    ref,
    () => ({
      generateRandomTower: () => {
        if (randomTowerRef.current) {
          debug("Generating random tower on mobile")
          setIsGenerating(true)
          randomTowerRef.current.generateRandomTower()
          setTimeout(() => {
            setIsGenerating(false)
          }, 800)
        }
      },
      activateEasterEgg: () => {
        if (randomTowerRef.current && randomTowerRef.current.activateEasterEgg) {
          randomTowerRef.current.activateEasterEgg()
        }
      },
    }),
    [],
  )

  // Function to handle Easter egg activation
  const handleEasterEgg = () => {
    if (randomTowerRef.current && randomTowerRef.current.activateEasterEgg) {
      randomTowerRef.current.activateEasterEgg()
    }
  }

  // Update the useEffect to use physical key codes
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if user is typing in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return
      }

      // Use physical key position for 'R' key to randomize tower
      if (matchesPhysicalKey(event, HOTKEYS.RANDOMIZE)) {
        if (randomTowerRef.current) {
          setIsGenerating(true)
          randomTowerRef.current.generateRandomTower()
          setTimeout(() => {
            setIsGenerating(false)
          }, 800)
        }
      }

      // Use physical key position for 'G' key to activate Easter Egg
      if (matchesPhysicalKey(event, HOTKEYS.EASTER_EGG)) {
        handleEasterEgg()
      }
    }

    // Add event listener
    window.addEventListener("keydown", handleKeyDown)
    debug("Keyboard event handler attached")

    // Clean up
    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [])

  // Function to handle tower height updates
  const handleTowerHeightChange = (height: number) => {
    // Add some padding to ensure no overlap
    const newHeight = height + 16
    setTowerHeight(newHeight)

    // Update max height if needed to prevent layout shifts
    if (newHeight > maxTowerHeight) {
      setMaxTowerHeight(newHeight)
    }
  }

  return (
    <div className="w-full flex flex-col items-center">
      {/* Tower aligned to center */}
      <div
        className="w-full flex items-center justify-center transition-height duration-300 ease-in-out"
        style={{ height: `${maxTowerHeight}px`, minHeight: `${towerHeight}px` }}
      >
        <div className="w-full max-w-[384px]">
          <div className="w-full flex justify-center items-center">
            <RandomTower
              ref={randomTowerRef}
              isMobileView={true}
              includeBaseCube={true}
              randomCubeRange={{ min: 1, max: 3 }}
              onHeightChange={handleTowerHeightChange}
              mobileAnimation={true}
              simplifiedAnimation={true}
              alignToTop={false}
            />
          </div>
        </div>
      </div>
    </div>
  )
})

MobileRandomTower.displayName = "MobileRandomTower"
